import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import { MarketplaceSubGroup } from "@shared/types";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";
import useNetPnlSeries from "features/newMarketplace/NewLook/useNetPnlSeries.ts";

interface NewLookSparklineProps {
  group: MarketplaceSubGroup;
  width?: number;
  height?: number;
}

/**
 * The month's net PnL as a row trend: a smooth line in the direction of the
 * month's result, like the dashboard's account trends.
 */
const NewLookSparkline = ({
  group,
  width = 72,
  height = 24,
}: NewLookSparklineProps) => {
  const tokens = getNewLookTokens(useTheme());
  const { points, change } = useNetPnlSeries(group, 30, 24);

  const path = useMemo(() => {
    if (points.length < 3) return undefined;
    const values = points.map((point) => point.value);
    const min = Math.min(...values);
    const span = Math.max(...values) - min || 1;
    const coords = points.map(
      (point, index) =>
        [
          (index / (points.length - 1)) * width,
          2 + (1 - (point.value - min) / span) * (height - 4),
        ] as Point
    );
    return monotonePath(coords);
  }, [points, width, height]);

  if (!path) return <span style={{ width }} />;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden={true}
      style={{ flex: "none", overflow: "visible" }}
    >
      <path
        d={path}
        fill="none"
        stroke={change ? directionColor(tokens, change) : tokens.accent}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default React.memo(NewLookSparkline);
